import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/lib';
import { EmailCampaignFormInput } from '../EmailCampaignValidation';
import { EmailCampaignMode } from '../EmailCampaignSteps';

type PreviewScheduleStepProps = {
  onBack: VoidFunction;
  formValues: EmailCampaignFormInput;
  onConfirmAndSend: (sendNow: boolean) => void;
  isSubmitting: boolean;
  mode?: EmailCampaignMode;
};

export const PreviewScheduleStep: FC<PreviewScheduleStepProps> = ({ onBack, formValues, onConfirmAndSend, isSubmitting, mode = 'create' }) => {
  const { t } = useTranslation('campaign');
  const { t: tCommon } = useTranslation('COMMON');
  const {
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<EmailCampaignFormInput>();
  const scheduleType = watch('scheduleType');
  const scheduleStart = watch('scheduleStart') as Date | undefined;
  const scheduleEnd = watch('scheduleEnd') as Date | undefined;
  const scheduleStartError = errors.scheduleStart?.message ? t(errors.scheduleStart.message as any) : undefined;
  const scheduleEndError = errors.scheduleEnd?.message ? t(errors.scheduleEnd.message as any) : undefined;

  const toDateTimeLocalValue = (date: Date) => {
    const pad = (value: number) => String(value).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    if (scheduleType !== 'schedule') {
      setValue('scheduleStart', undefined, { shouldTouch: true, shouldValidate: true });
      setValue('scheduleEnd', undefined, { shouldTouch: true, shouldValidate: true });
    }
  }, [scheduleType, setValue]);

  const handleScheduleTypeChange = (type: 'sendNow' | 'schedule') => {
    setValue('scheduleType', type, { shouldTouch: true });

    if (type === 'schedule') {
      const defaultStart = new Date();
      defaultStart.setSeconds(0);
      defaultStart.setMilliseconds(0);

      const defaultEnd = new Date(defaultStart);
      defaultEnd.setHours(defaultEnd.getHours() + 1);

      setValue('scheduleStart', defaultStart, { shouldTouch: true, shouldValidate: true });
      setValue('scheduleEnd', defaultEnd, { shouldTouch: true, shouldValidate: true });
    } else {
      setValue('scheduleStart', undefined);
      setValue('scheduleEnd', undefined);
    }
  };

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('whatsappPreview')}</h2>
        <p className='mt-2 text-sm text-gray-600'>{t('whatsappPreviewDescription')}</p>
      </div>

      <div className='grid gap-6'>
        <div className='space-y-4 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm'>
          <p className='text-sm uppercase tracking-[0.18em] text-brand-600'>{t('preview')}</p>
          <div className='space-y-3'>
            <div>
              <p className='text-sm text-gray-500'>{t('enterCampaignTitleLabel')}</p>
              <p className='text-base font-medium text-gray-900'>{formValues.name || t('previewMissingTitle')}</p>
            </div>
            <div>
              <p className='text-sm text-gray-500'>{t('enterCampaignDescriptionLabel')}</p>
              <p className='text-base text-gray-900'>{formValues.description || t('previewMissingDescription')}</p>
            </div>
            <div>
              <p className='text-sm text-gray-500'>{t('template')}</p>
              <p className='text-base text-gray-900'>{formValues.templateName || t('templateNotSelected')}</p>
            </div>
          </div>
        </div>

        <div className='rounded-3xl border border-gray-200 bg-gray-50 p-6'>
          <div className='grid gap-4'>
            <div className='grid grid-cols-2 gap-3'>
              <button
                type='button'
                className={
                  scheduleType === 'sendNow'
                    ? 'rounded-2xl border border-brand-600 bg-brand-50 px-4 py-3 text-left text-brand-900'
                    : 'rounded-2xl border border-gray-300 bg-white px-4 py-3 text-left text-gray-700'
                }
                onClick={() => handleScheduleTypeChange('sendNow')}
              >
                <p className='font-semibold'>{t('sendNow')}</p>
                <p className='text-sm text-gray-500'>{t('sendImmediately')}</p>
              </button>
              <button
                type='button'
                className={
                  scheduleType === 'schedule'
                    ? 'rounded-2xl border border-brand-600 bg-brand-50 px-4 py-3 text-left text-brand-900'
                    : 'rounded-2xl border border-gray-300 bg-white px-4 py-3 text-left text-gray-700'
                }
                onClick={() => handleScheduleTypeChange('schedule')}
              >
                <p className='font-semibold'>{t('schedule')}</p>
                <p className='text-sm text-gray-500'>{t('scheduleForLater')}</p>
              </button>
            </div>
            {scheduleType === 'schedule' && (
              <div className='flex items-center justify-start gap-4 rounded-3xl border border-brand-200 bg-brand-50 p-6'>
                <label className='grid w-full cursor-text gap-2'>
                  <span className='text-sm font-semibold text-brand-900'>{t('scheduleStart')}</span>
                  <input
                    id='scheduleStart'
                    type='datetime-local'
                    value={scheduleStart ? toDateTimeLocalValue(scheduleStart) : ''}
                    min={toDateTimeLocalValue(new Date())}
                    step={900}
                    className='w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-brand-600 focus:outline-none'
                    onChange={(event) => {
                      const value = event.target.value;
                      const startDate = value ? new Date(value) : undefined;
                      setValue('scheduleStart', startDate, { shouldTouch: true, shouldValidate: true });

                      if (startDate) {
                        const currentEnd = getValues('scheduleEnd') as Date | undefined;
                        const defaultEnd = new Date(startDate);
                        defaultEnd.setHours(defaultEnd.getHours() + 1);

                        if (!currentEnd || currentEnd <= startDate) {
                          setValue('scheduleEnd', defaultEnd, { shouldTouch: true, shouldValidate: true });
                        }
                      }
                    }}
                  />
                  {scheduleStartError && <p className='text-sm text-red-600'>{scheduleStartError}</p>}
                </label>

                <label className='grid w-full cursor-text gap-2' htmlFor='scheduleEnd'>
                  <span className='text-sm font-semibold text-brand-900'>{t('scheduleEnd')}</span>
                  <input
                    id='scheduleEnd'
                    type='datetime-local'
                    value={scheduleEnd ? toDateTimeLocalValue(scheduleEnd) : ''}
                    min={scheduleStart ? toDateTimeLocalValue(scheduleStart) : toDateTimeLocalValue(new Date())}
                    step={900}
                    className='w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-brand-600 focus:outline-none'
                    onChange={(event) => {
                      const value = event.target.value;
                      setValue('scheduleEnd', value ? new Date(value) : undefined, { shouldTouch: true, shouldValidate: true });
                    }}
                  />
                  {scheduleEndError && <p className='text-sm text-red-600'>{scheduleEndError}</p>}
                </label>
              </div>
            )}
            {scheduleType === 'sendNow' && (
              <div className='rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700'>{t('sendNowDescription')}</div>
            )}
          </div>
        </div>
      </div>

      <div className='flex items-center justify-between gap-4'>
        <Button intent='secondary' text={tCommon('back')} onClick={onBack} />
        <div className='flex items-center gap-4'>
          <Button
            className='w-full max-w-[220px]'
            text={mode === 'edit' ? t('saveChangesAsDraft') : 'Save as Draft'}
            onClick={() => onConfirmAndSend(false)}
            isLoading={isSubmitting}
            disabled={isSubmitting}
          />
          <Button
            className='w-full max-w-[220px]'
            text={mode === 'edit' ? t('saveChanges') : t('confirmAndSend')}
            onClick={() => onConfirmAndSend(true)}
            isLoading={isSubmitting}
            disabled={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};
